/**
 * Cinematic motion primitives for the YugOS landing page.
 * Built on framer-motion — shared easing, reveals, parallax tilt,
 * grain texture, scroll progress, and the global click ripple.
 */
import React, { useEffect } from "react";
import {
    motion,
    useMotionValue,
    useReducedMotion,
    useScroll,
    useSpring,
} from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

/** FadeUp — the standard reveal. Fades + rises once when scrolled into view. */
export function FadeUp({ children, delay = 0, y = 26, className = "", ...rest }) {
    return (
        <motion.div
            initial={{ opacity: 0, y }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.9, delay, ease: EASE }}
            className={className}
            {...rest}
        >
            {children}
        </motion.div>
    );
}

/** WordReveal — staggers each word of a headline up from a masked line. */
export function WordReveal({ text, className = "", delay = 0, as: Tag = "span" }) {
    const words = text.split(" ");
    const MTag = motion[Tag] || motion.span;
    return (
        <MTag
            className={className}
            aria-label={text}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
        >
            {words.map((w, i) => (
                <span key={`${w}-${i}`} className="inline-block overflow-hidden align-bottom">
                    <motion.span
                        className="inline-block"
                        variants={{
                            hidden: { y: "110%" },
                            visible: {
                                y: 0,
                                transition: { duration: 0.8, delay: delay + i * 0.055, ease: EASE },
                            },
                        }}
                    >
                        {w}
                        {i < words.length - 1 ? "\u00A0" : ""}
                    </motion.span>
                </span>
            ))}
        </MTag>
    );
}

/** ScrollProgress — 2px sienna→indigo bar pinned above everything. */
export function ScrollProgress() {
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, { stiffness: 150, damping: 26, mass: 0.4 });
    return (
        <motion.div
            data-testid="scroll-progress"
            aria-hidden="true"
            className="fixed inset-x-0 top-0 z-[70] h-[2px] origin-left bg-gradient-to-r from-[#c2410c] to-[#4353e4]"
            style={{ scaleX }}
        />
    );
}

/** Tilt — subtle mouse-follow 3D depth for hero/product frames. */
export function Tilt({ children, max = 4.5, className = "" }) {
    const reduce = useReducedMotion();
    const rx = useMotionValue(0);
    const ry = useMotionValue(0);
    const srx = useSpring(rx, { stiffness: 110, damping: 18 });
    const sry = useSpring(ry, { stiffness: 110, damping: 18 });

    if (reduce) return <div className={className}>{children}</div>;

    const onMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        ry.set(px * max * 2);
        rx.set(-py * max * 2);
    };
    const onLeave = () => {
        rx.set(0);
        ry.set(0);
    };
    return (
        <div style={{ perspective: 1500 }} className={className}>
            <motion.div
                style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
                onPointerMove={onMove}
                onPointerLeave={onLeave}
            >
                {children}
            </motion.div>
        </div>
    );
}

const GRAIN_URI =
    "data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E";

/** Grain — physical film-grain overlay for dark sections. */
export function Grain({ className = "", opacity = 0.06 }) {
    return (
        <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-0 ${className}`}
            style={{ backgroundImage: `url("${GRAIN_URI}")`, opacity, mixBlendMode: "overlay" }}
        />
    );
}

/**
 * ClickRipple — global cursor pulse on click.
 * Skips inputs, dropdown items, and interactive selects.
 */
export function ClickRipple() {
    useEffect(() => {
        if (typeof window === "undefined") return;
        const media = window.matchMedia?.("(prefers-reduced-motion: reduce)");
        if (media?.matches) return;

        const onDown = (e) => {
            const t = e.target;
            if (
                t &&
                t.closest &&
                t.closest("input, textarea, select, [role='menuitem'], [contenteditable='true']")
            ) {
                return;
            }
            const ripple = document.createElement("span");
            ripple.className = "click-ripple";
            let el = t;
            let onDark = false;
            for (let i = 0; i < 6 && el && el.getBoundingClientRect; i++) {
                const bg = window.getComputedStyle(el).backgroundColor;
                if (bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent") {
                    const m = bg.match(/\d+/g);
                    if (m) {
                        const [r, g, b] = m.map(Number);
                        const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
                        if (lum < 90) onDark = true;
                        break;
                    }
                }
                el = el.parentElement;
            }
            if (onDark) ripple.classList.add("click-ripple--light");
            ripple.style.left = `${e.clientX}px`;
            ripple.style.top = `${e.clientY}px`;
            document.body.appendChild(ripple);
            setTimeout(() => ripple.remove(), 550);
        };

        window.addEventListener("pointerdown", onDown, { passive: true });
        return () => window.removeEventListener("pointerdown", onDown);
    }, []);
    return null;
}
